import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getTeamAccessEntitlements } from '@/pages/api/scribeAPIV2';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { type components } from '@heidi/schema/ml-scribe';
import { GET_TEAM_ACCESS_ENTITLEMENTS } from '@heidi/workspace/contracts';
import { skipToken, useQuery } from '@tanstack/react-query';

// The key string now has one declaration site repo-wide —
// `@heidi/workspace/contracts` (EN4, ARCFE-1025) — because the entitlement
// write hooks moved into headless `/data` and cannot reach `@/hooks/…`.
// Re-exported (not re-declared) so every existing importer of this module
// keeps resolving the same identity. Imported as well as re-exported: this
// module builds the key locally too.
export { GET_TEAM_ACCESS_ENTITLEMENTS };

export type TeamLevelAccessEntitlementsResponse =
  components['schemas']['TeamLevelAccessEntitlementsResponse'];

/**
 * GET /api/v2/ml-scribe/access-entitlements/team/{team_id}
 * List effective access entitlements for the team (team + org merge). Caller must be a team admin.
 */
export function useQueryTeamAccessEntitlements(teamId: string | undefined) {
  const { isAuthenticated } = useIsAuthenticated();

  return useQuery({
    queryKey: [GET_TEAM_ACCESS_ENTITLEMENTS, teamId],
    enabled: isAuthenticated && !!teamId,
    queryFn:
      isAuthenticated && teamId
        ? async () => {
            const { data } = await getTeamAccessEntitlements(teamId);
            return data;
          }
        : skipToken,
  });
}

export async function invalidateTeamAccessEntitlements(teamId: string) {
  await reactQueryClient.invalidateQueries({
    queryKey: [GET_TEAM_ACCESS_ENTITLEMENTS, teamId],
  });
}
